import { ExternalLink, Package, ShoppingCart, Link2, Star, Loader2 } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { SearchResult } from "@/types/search";
import { cn } from "@/lib/utils";

interface MobileResultCardProps {
  result: SearchResult;
  onClick?: () => void;
  onAddFavorite?: (result: SearchResult) => void;
  isFavorite?: boolean;
  isAddingFavorite?: boolean;
}

const MobileResultCard = ({ result, onClick, onAddFavorite, isFavorite, isAddingFavorite }: MobileResultCardProps) => {
  const title = result.title || result.name || "Pièce automobile";
  // isInStock is now normalized by the API validation layer
  const isInStock = result.isInStock ?? false;
  // @ts-ignore - crossReferenced is added dynamically by backend
  const isCrossReferenced = result.crossReferenced === true;

  // Safely normalize price - distinguish between valid 0€ and invalid/missing price
  const rawPrice = (result as any)?.price?.current?.price as unknown;
  let normalizedPrice: number | null = null;

  if (typeof rawPrice === "number" && !isNaN(rawPrice)) {
    normalizedPrice = rawPrice;
  } else if (typeof rawPrice === "string" && rawPrice.trim()) {
    const parsed = Number(rawPrice.replace(",", "."));
    if (!isNaN(parsed)) {
      normalizedPrice = parsed;
    }
  }

  const hasPriceAvailable = typeof normalizedPrice === "number";

  return (
    <Card
      className="p-4 space-y-3 hover-scale cursor-pointer active:scale-95 transition-all"
      onClick={onClick}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <Badge variant="outline" className="text-xs">
              {result.brand}
            </Badge>
            {isCrossReferenced && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Badge
                      variant="secondary"
                      className="text-xs bg-blue-500/10 text-blue-600 border-blue-500/20 flex items-center gap-1"
                    >
                      <Link2 className="h-3 w-3" />
                      Via OEM
                    </Badge>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="text-xs">
                      Résultat trouvé via référence OEM correspondante
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
            <span className="text-xs text-muted-foreground truncate">
              {result.refNumber}
            </span>
          </div>
          <h3 className="font-semibold text-sm line-clamp-2 mb-2">{title}</h3>
        </div>

        <div className="text-right flex-shrink-0">
          {hasPriceAvailable ? (
            <div className="text-xl font-bold text-primary">
              {normalizedPrice!.toFixed(2)}€
            </div>
          ) : (
            <div className="text-sm font-medium text-muted-foreground italic">
              Prix indisponible
            </div>
          )}
        </div>
      </div>

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Package className="h-4 w-4 text-muted-foreground" />
          <span className="text-xs text-muted-foreground">
            {result.platform}
          </span>
        </div>

        <div className="flex items-center gap-2">
          <Badge
            variant={isInStock ? "default" : "secondary"}
            className={cn(
              "text-xs pointer-events-none",
              isInStock
                ? "bg-green-500/10 text-green-600 border-green-500/20"
                : "bg-red-500/10 text-red-600 border-red-500/20"
            )}
          >
            {isInStock ? (
              <span className="flex items-center gap-1">
                <ShoppingCart className="h-3 w-3" />
                Stock
              </span>
            ) : (
              "Rupture"
            )}
          </Badge>

          {onAddFavorite && (
            <Button
              variant="ghost"
              size="icon"
              className={cn(
                "h-8 w-8 transition-all",
                isFavorite && "text-yellow-500"
              )}
              onClick={(e) => {
                e.stopPropagation();
                onAddFavorite(result);
              }}
              disabled={isAddingFavorite}
            >
              {isAddingFavorite ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Star 
                  className={cn(
                    "h-4 w-4 transition-all",
                    isFavorite && "fill-current animate-[bounce_0.5s_ease-in-out]"
                  )} 
                />
              )}
            </Button>
          )}

          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={(e) => {
              e.stopPropagation();
              if (result.websiteUrl) {
                window.open(result.websiteUrl, "_blank", "noopener,noreferrer");
              }
            }}
          >
            <ExternalLink className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </Card>
  );
};

export default MobileResultCard;
